import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Footer from "../../core/common/footer/Footer";
import bulbImage from "../../images/buld.png";
import {
  apibaseurl,
  getPersonalDetails,
  updatePersonalDetails,
} from "../../environment";
import useApi from "../../core/customhook/useApi";
import SnipperLoader from "../../core/common/loader/SnipperLoader";
import z from "zod";
import { all_routes } from "../router/all_routes";
import { useSelector } from "react-redux";
import { RootState } from "../../core/data/redux/store";

const CV_MAX_SIZE = 10 * 1024 * 1024; // 3MB
const CV_ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const personalDetailsSchema = z.object({
  first_name: z.string().trim().min(1, "First name is required"),
  middle_name: z.string().trim().optional(),
  last_name: z.string().trim().min(1, "Last name is required"),
  email: z.string().trim().optional(),
  phone: z
    .string()
    .trim()
    .min(1, "Mobile number is required")
    .refine((val) => /^\d{10}$/.test(val), {
      message: "Enter a valid 10-digit mobile number",
    }),
  dob: z.string().trim().min(1, "Date of birth is required"),
  gender: z
    .string()
    .trim()
    .min(1, "Please select a gender")
    .refine((val) => ["Male", "Female", "Other"].includes(val), {
      message: "Please select a valid gender",
    }),
  alternative_phone: z
    .string()
    .trim()
    .optional()
    .refine((val) => !val || /^\d{10}$/.test(val), {
      message: "Enter a valid 10-digit mobile number",
    }),
  father_or_mother_name: z
    .string()
    .trim()
    .min(1, "Father's name/ Mother's name is required"),
});

type PersonalDetailsFormData = z.infer<typeof personalDetailsSchema>;
type FormErrors = Partial<Record<keyof PersonalDetailsFormData, string>>;

const PersonalInfoForm = () => {
  const PersonalDetailAPI = useApi<any>(getPersonalDetails, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const UpdateDetailAPI = useApi<any>(updatePersonalDetails, {
    method: "PUT",
    autoCall: false,
    handleGlobalError: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const [formData, setFormData] = useState<PersonalDetailsFormData>({
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    phone: "",
    dob: "",
    gender: "",
    alternative_phone: "",
    father_or_mother_name: "",
  });

  // CV upload state
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [existingCv, setExistingCv] = useState<string>("");
  const [cvError, setCvError] = useState<string>("");
  const cvInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const templates = useSelector(
    (state: RootState) => state?.package?.templates,
  );

  const addressCheck = templates?.find(
    (item) => item.check_id === "9a6fd659-63ad-4d92-ad3c-ab5bff7bb86d",
  );

  const getProfileData = async () => {
    try {
      const response = await PersonalDetailAPI.callApi();

      if (response?.success) {
        const data = response.data;

        localStorage.setItem("userPersonalData", JSON.stringify(data));

        setFormData({
          first_name: data?.first_name || "",
          middle_name: data?.middle_name || "",
          last_name: data?.last_name || "",
          email: data?.email || "",
          phone: data?.phone || "",
          dob: data?.dob ? new Date(data.dob).toISOString().split("T")[0] : "",
          gender: data?.gender || "",
          alternative_phone: data?.alternative_phone || "",
          father_or_mother_name: data?.father_or_mother_name || "",
        });

        // NOTE: rename `cv_url` to match the field your backend returns
        setExistingCv(data?.resume_file_key || "");
      }
    } catch (error: any) {
      // Swal.fire({
      //   icon: "error",
      //   title: "Error",
      //   text:
      //     error?.response?.data?.error ||
      //     error?.message ||
      //     "Failed to fetch profile",
      // });
      console.log(error);
    }
  };

  useEffect(() => {
    getProfileData();
  }, []);

  // HANDLE INPUT CHANGE
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear the field's error as soon as the user edits it
    if (errors[name as keyof PersonalDetailsFormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // HANDLE CV FILE CHANGE
  const handleCvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!CV_ALLOWED_TYPES.includes(file.type)) {
      setCvError("Only PDF, DOC or DOCX files are allowed");
      setCvFile(null);
      e.target.value = "";
      return;
    }

    if (file.size > CV_MAX_SIZE) {
      setCvError("File size must be 10MB or less");
      setCvFile(null);
      e.target.value = "";
      return;
    }

    setCvError("");
    setCvFile(file);
  };

  const handleUpdate = async () => {
    const result = personalDetailsSchema.safeParse(formData);

    let hasError = false;

    if (!result.success) {
      const fieldErrors: FormErrors = {};
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as keyof PersonalDetailsFormData;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      });
      setErrors(fieldErrors);
      hasError = true;
    } else {
      setErrors({});
    }

    // CV is required only if the user has none on file yet
    // (remove this block if the CV should be optional)
    if (!cvFile && !existingCv) {
      setCvError("Please upload your CV");
      hasError = true;
    }

    if (hasError || cvError) return;

    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        payload.append(key, value ?? "");
      });
      if (cvFile) payload.append("cv", cvFile);

      const response = await UpdateDetailAPI.callApi(payload);

      if (response?.success) {
        setCvFile(null);

        if (cvInputRef.current) {
          cvInputRef.current.value = "";
        }

        getProfileData();

        const result = await Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.message || "Profile updated successfully",
          confirmButtonText: "OK",
        });

        if (result.isConfirmed && addressCheck) {
          navigate(all_routes.addressInfo);
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: response?.error || response?.message || "Something went wrong",
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong",
      });
    }
  };

  const getdataLoading = PersonalDetailAPI.loading;
  const updatedataLoading = UpdateDetailAPI.loading;
  const getCvUrl = (key: string) => {
    if (!key) return "";
    if (key.startsWith("http")) return key; // already a full URL
    return `${apibaseurl.replace(/\/+$/, "")}/uploads/${key.replace(/^\/+/, "")}`;
  };
  return (
    <>
      <div className="page-wrapper">
        {getdataLoading ? (
          <SnipperLoader />
        ) : (
          <div className="content">
            <div className="row">
              <div className="col-md-8">
                <form className="card overflow-hidden p-4">
                  {/* Heading */}
                  <div className="border-bottom pb-3 mb-4">
                    <h5 className="fw-semibold mb-0">Personal Information</h5>
                  </div>

                  <div className="row">
                    {/* First Name */}
                    <div className="col-md-6 mb-2 mt-3">
                      <label className="form-label fw-medium">
                        First Name <span className="text-danger">*</span>
                      </label>

                      <input
                        type="text"
                        className={`form-control ${errors.first_name ? "is-invalid" : ""}`}
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        placeholder="Enter First Name"
                      />
                      {errors.first_name && (
                        <div className="invalid-feedback">
                          {errors.first_name}
                        </div>
                      )}
                    </div>

                    {/* Middle Name */}
                    <div className="col-md-6 mb-2 mt-3">
                      <label className="form-label fw-medium">
                        Middle Name
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        name="middle_name"
                        value={formData.middle_name}
                        onChange={handleChange}
                        placeholder="Enter Middle Name"
                      />
                    </div>

                    {/* Last Name */}
                    <div className="col-md-6 mb-2 mt-3">
                      <label className="form-label fw-medium">
                        Last Name <span className="text-danger">*</span>
                      </label>

                      <input
                        type="text"
                        className={`form-control ${errors.last_name ? "is-invalid" : ""}`}
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        placeholder="Enter Last Name"
                      />
                      {errors.last_name && (
                        <div className="invalid-feedback">
                          {errors.last_name}
                        </div>
                      )}
                    </div>

                    {/* Parent Name */}
                    <div className="col-md-6 mb-2 mt-3">
                      <label className="form-label fw-medium">
                        Father's name/ Mother's name{" "}
                        <span className="text-danger">*</span>
                      </label>

                      <input
                        type="text"
                        className={`form-control ${
                          errors.father_or_mother_name ? "is-invalid" : ""
                        }`}
                        name="father_or_mother_name"
                        value={formData.father_or_mother_name}
                        onChange={handleChange}
                        placeholder="Enter Parent Name"
                      />
                      {errors.father_or_mother_name && (
                        <div className="invalid-feedback">
                          {errors.father_or_mother_name}
                        </div>
                      )}
                    </div>

                    {/* DOB */}
                    <div className="col-md-6 mb-2 mt-3">
                      <label className="form-label fw-medium">
                        Date of Birth (mm-dd-yyyy){" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className={`form-control ${errors.dob ? "is-invalid" : ""}`}
                        name="dob"
                        value={formData.dob}
                        onChange={handleChange}
                      />
                      {errors.dob && (
                        <div className="invalid-feedback">{errors.dob}</div>
                      )}
                    </div>

                    {/* Gender */}
                    <div className="col-md-6 mb-2 mt-3">
                      <label className="form-label fw-medium">
                        Gender <span className="text-danger">*</span>
                      </label>

                      <select
                        className={`form-select ${errors.gender ? "is-invalid" : ""}`}
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                      {errors.gender && (
                        <div className="invalid-feedback">{errors.gender}</div>
                      )}
                    </div>

                    {/* Mobile Number */}
                    <div className="col-md-6 mb-2 mt-3">
                      <label className="form-label fw-medium">
                        Mobile Number <span className="text-danger">*</span>
                      </label>

                      <div className="input-group">
                        <span className="input-group-text">+91 (India)</span>

                        <input
                          type="text"
                          className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Enter Mobile Number"
                        />
                      </div>
                      {errors.phone && (
                        <div className="text-danger small mt-1">
                          {errors.phone}
                        </div>
                      )}
                    </div>

                    {/* Alternate Number */}
                    <div className="col-md-6 mb-2 mt-3">
                      <label className="form-label fw-medium">
                        Alternative Phone Number
                      </label>

                      <div className="input-group">
                        <span className="input-group-text">+91 (India)</span>

                        <input
                          type="text"
                          className={`form-control ${
                            errors.alternative_phone ? "is-invalid" : ""
                          }`}
                          name="alternative_phone"
                          value={formData.alternative_phone}
                          onChange={handleChange}
                          placeholder="Enter Alternate Number"
                        />
                      </div>
                      {errors.alternative_phone && (
                        <div className="text-danger small mt-1">
                          {errors.alternative_phone}
                        </div>
                      )}
                    </div>

                    {/* CV Upload */}
                    <div className="col-md-12 mb-3 mt-3">
                      <label className="form-label fw-medium">
                        Upload CV <span className="text-danger">*</span>
                      </label>

                      <input
                        ref={cvInputRef}
                        type="file"
                        className={`form-control ${cvError ? "is-invalid" : ""}`}
                        accept=".pdf,.doc,.docx"
                        onChange={handleCvChange}
                      />
                      {cvError && (
                        <div className="invalid-feedback">{cvError}</div>
                      )}

                      <div className="form-text">
                        Accepted formats: PDF, DOC, DOCX (max 10MB)
                      </div>

                      {cvFile && (
                        <div className="small text-success mt-1">
                          Selected: {cvFile.name}
                        </div>
                      )}

                      {!cvFile && existingCv && (
                        <div className="small mt-1">
                          Current CV:{" "}
                          <a
                            href={getCvUrl(existingCv)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View uploaded CV
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Submit */}
                    <div className="col-md-12">
                      <button
                        type="button"
                        className="btn btn-primary px-4"
                        onClick={handleUpdate}
                        disabled={updatedataLoading}
                      >
                        {updatedataLoading ? "Updating..." : "Update Profile"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
              <div className="col-md-4">
                <div
                  style={{
                    position: "sticky",
                    top: "60px",
                    alignSelf: "flex-start",
                  }}
                >
                  <div className="card border-0 shadow-sm h-100">
                    {/* Header */}
                    <div
                      className="text-center py-4"
                      style={{ backgroundColor: "#f7f1e3" }}
                    >
                      <div
                        className="d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle bg-white"
                        style={{ width: "70px", height: "70px" }}
                      >
                        <img src={bulbImage} alt="buld" />
                      </div>

                      <h6 className="fw-bold mb-0">Instructions</h6>
                    </div>

                    {/* Body */}
                    <div className="card-body">
                      {/* Instruction 1 */}
                      <div className="d-flex mb-4">
                        <div
                          className="rounded me-3 mt-1 flex-shrink-0"
                          style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#f4c542",
                          }}
                        ></div>

                        <div>
                          <h6 className="mb-1 fw-semibold">Instruction 1</h6>

                          <p className="text-muted mb-0 small">
                            Kindly fill in and submit the form at the earliest,
                            this form will expire in 15 days, in case there is
                            no activity.
                          </p>
                        </div>
                      </div>

                      {/* Instruction 2 */}
                      <div className="d-flex mb-4">
                        <div
                          className="rounded me-3 mt-1 flex-shrink-0"
                          style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#f4c542",
                          }}
                        ></div>

                        <div>
                          <h6 className="mb-1 fw-semibold">Instruction 2</h6>

                          <p className="text-muted mb-0 small">
                            In case you are not joining the company, kindly
                            inform the HR to stop your background verification
                            process.
                          </p>
                        </div>
                      </div>

                      {/* Instruction 3 */}
                      <div className="d-flex mb-4">
                        <div
                          className="rounded me-3 mt-1 flex-shrink-0"
                          style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#f4c542",
                          }}
                        ></div>

                        <div>
                          <h6 className="mb-1 fw-semibold">Instruction 3</h6>

                          <p className="text-muted mb-0 small">
                            Please provide all the information/documentation at
                            the time of filling the form to avoid any delay in
                            processing. In case you do not have the document,
                            you can still proceed and we will raise a request at
                            a later timeframe (if required)
                          </p>
                        </div>
                      </div>

                      {/* Instruction 4 */}
                      <div className="d-flex">
                        <div
                          className="rounded me-3 mt-1 flex-shrink-0"
                          style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#f4c542",
                          }}
                        ></div>

                        <div>
                          <h6 className="mb-1 fw-semibold">Instruction 4</h6>

                          <p className="text-muted mb-0 small">
                            Please upload photograph, Front facing with clear
                            background. 10MB size limit with Jpeg,Png and jpg
                            format only.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <Footer />
      </div>
    </>
  );
};

export default PersonalInfoForm;
